// Your business → Coverage and Filters (Neil, 11 Oct 2026: split from "Coverage & filters" into two
// pages): Coverage is the councils the company works in (local authority districts, as xenscope does;
// up to the company's limit, 10 by default); Filters is which buildings it wants (system sizes,
// sectors, payback, electricity data, the weekly email). One record and one save (PUT /v1/coverage),
// so each page shows its own part and the shortlist summary; the shortlist (Search) reads it.
//
// Councils are chosen on a map (as xenscope) or by search, and LOCK when saved: a dialog asks the
// user to tick that they understand. Saved councils can't be removed, but more can be added up to
// the company's limit, each locking in when saved (Neil, 14 Oct 2026). Filters stay editable.
// Owners and admins edit; members see it read-only.
//
// Private commercial (BUILD-PLAN-PRIVATE-COMMERCIAL.md PC5.1): Filters saves ownership (All, Public
// or Private; "any" when absent). The choice shows only where the API offers it (options.ownership),
// or while a saved choice other than All is in force, so where private properties are off the page
// looks as it did.
import { useEffect, useMemo, useState, type FormEvent } from "react";
import { Check, Lock, Plus, X } from "lucide-react";
import { Link } from "react-router-dom";
import { useApi, type Coverage, type CoverageResponse, type OwnershipFilter } from "../api";
import { ConfirmDialog } from "../components/ConfirmDialog";
import { CouncilMap } from "../components/CouncilMap";
import { Field, Segmented, Toggle } from "../components/controls";
import "../coverage.css";
import "../widgets.css";

/** Payback choices: any, or up to N years (the API allows 3–15). */
const PAYBACK_CHOICES: (number | null)[] = [null, 5, 6, 7, 8, 10, 12];

/** Public / Private: the three choices when the API doesn't send its own labels. */
const OWNERSHIP_CHOICES: [OwnershipFilter, string][] = [
  ["any", "All"],
  ["public", "Public sector"],
  ["private", "Private sector"],
];
/** The saved Public / Private choice in words, for the summary. */
const OWNERSHIP_SUMMARY: Record<OwnershipFilter, string> = {
  any: "Public and private sector",
  public: "Public sector only",
  private: "Private sector only",
};

export function CoveragePage({ part = "coverage" }: { part?: "coverage" | "filters" }) {
  // Filters needs the districts saved first: one record, and the first save locks them
  const isFilters = part === "filters";
  const api = useApi();
  const [data, setData] = useState<CoverageResponse | null>(null);
  const [draft, setDraft] = useState<Coverage | null>(null);
  const [message, setMessage] = useState<{ tone: "success" | "danger"; text: string } | null>(null);
  const [saving, setSaving] = useState(false);
  // First save: the lock confirmation dialog and its tick box
  const [confirming, setConfirming] = useState(false);
  const [accepted, setAccepted] = useState(false);

  useEffect(() => {
    api
      .getCoverage()
      .then((r) => {
        setData(r);
        setDraft(r.coverage);
      })
      .catch((e: Error) => setMessage({ tone: "danger", text: e.message }));
  }, [api]);

  const councilNames = useMemo(() => Object.fromEntries((data?.options.councils ?? []).map((c) => [c.code, c.name])), [data]);
  const dirty = Boolean(data && draft && JSON.stringify(draft) !== JSON.stringify(data.coverage));
  const readOnly = Boolean(data && !data.canEdit);
  const locked = Boolean(data?.councilsLockedAt);
  // The councils already saved (and locked): fixed. Below the limit, more can be added and saved
  const limit = data?.options.limits.councils ?? 0;
  const lockedCouncils = useMemo(() => (locked ? data?.coverage.councils ?? [] : []), [locked, data]);
  const canAdd = !readOnly && (!locked || lockedCouncils.length < limit);
  const added = (draft?.councils ?? []).filter((c) => !lockedCouncils.includes(c));
  /** A new council list, never without the locked ones (the map's clicks could otherwise remove them). */
  const withLocked = (councils: string[]) => [...lockedCouncils, ...councils.filter((c) => !lockedCouncils.includes(c))];
  // Public / Private: offered where the API sends the choice, or kept visible while one is saved
  const ownership = draft?.filters.ownership ?? "any";
  const showOwnership = Boolean(data?.options.ownership?.length || ownership !== "any");
  const ownershipOptions: [OwnershipFilter, string][] = data?.options.ownership?.length
    ? data.options.ownership.map((o) => [o.key, o.label] as [OwnershipFilter, string])
    : OWNERSHIP_CHOICES;

  // Live building counts for the draft (saved or not), half a second after the last change
  const [counts, setCounts] = useState<{ total: number; byDistrict: Record<string, number> } | null>(null);
  useEffect(() => {
    if (!draft) return;
    const t = window.setTimeout(() => {
      api
        .countBuildings(draft)
        .then(setCounts)
        .catch(() => setCounts(null));
    }, 500);
    return () => window.clearTimeout(t);
  }, [api, draft]);

  const update = (patch: Partial<Coverage>) => {
    setDraft((d) => (d ? { ...d, ...patch } : d));
    setMessage(null);
  };
  const updateFilters = (patch: Partial<Coverage["filters"]>) => setDraft((d) => (d ? { ...d, filters: { ...d.filters, ...patch } } : d));

  /** Add or remove one value from a list filter. */
  const toggleIn = (list: string[], value: string) => (list.includes(value) ? list.filter((v) => v !== value) : [...list, value]);

  /** Save button: any new councils ask for the lock confirmation first; otherwise it saves the filters. */
  function submit(e: FormEvent) {
    e.preventDefault();
    if (!draft) return;
    if (draft.councils.length === 0) {
      setMessage({ tone: "danger", text: "Choose at least one Local Authority District." });
      return;
    }
    if (added.length > 0) {
      setAccepted(false);
      setConfirming(true);
      return;
    }
    void save(false);
  }

  async function save(acceptCouncilLock: boolean) {
    if (!draft) return;
    setSaving(true);
    setMessage(null);
    try {
      const r = await api.saveCoverage(draft, acceptCouncilLock);
      setData(r);
      setDraft(r.coverage);
      setMessage({
        tone: "success",
        text: acceptCouncilLock
          ? "Coverage saved and your new Local Authority Districts are locked in. Your shortlist uses them from now on."
          : "Filters saved.",
      });
    } catch (err) {
      setMessage({ tone: "danger", text: (err as Error).message });
    } finally {
      setSaving(false);
      setConfirming(false);
    }
  }

  return (
    <div className="page">
      <div className="page-head">
        <h1>{isFilters ? "Filters" : "Coverage"}</h1>
        <p>
          {isFilters
            ? "Choose which properties you want to see in the Local Authority Districts you cover. You can change these at any time."
            : "Choose the Local Authority Districts you work in. Your shortlist only shows properties in them."}
        </p>
      </div>

      {message && <div className={`flash flash-${message.tone}`}>{message.text}</div>}
      {readOnly && <div className="flash flash-info">Only the account owner or an admin can change coverage.</div>}

      {!draft || !data ? (
        !message && <p className="muted">Loading…</p>
      ) : (
        <form className="coverage" onSubmit={submit}>
          <fieldset className="coverage-main" disabled={readOnly}>
            {/* Where: Local Authority Districts, on the map or by search (the Coverage page) */}
            {!isFilters && (
            <section className="card">
              <h2>Where you work</h2>
              {locked && (
                <p className="coverage-locked">
                  <Lock size={14} aria-hidden="true" /> {lockedCouncils.length} of {limit} locked in since{" "}
                  {new Date(data.councilsLockedAt!).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })}.{" "}
                  {lockedCouncils.length < limit
                    ? `You can add ${limit - lockedCouncils.length} more; each locks in when you save. Contact us if you need to change a locked one.`
                    : "That's your limit. Contact us if you need to change your Local Authority Districts."}
                </p>
              )}
              <CouncilMap selected={draft.councils} limit={limit} readOnly={!canAdd} onChange={(councils) => update({ councils: withLocked(councils) })} />
              {canAdd ? (
                <CouncilPicker
                  selected={draft.councils}
                  fixed={lockedCouncils}
                  options={data.options.councils}
                  names={councilNames}
                  limit={limit}
                  onChange={(councils) => update({ councils: withLocked(councils) })}
                />
              ) : (
                <div className="chip-row">
                  {draft.councils.map((c) => (
                    <span key={c} className="chip is-on">
                      <Lock size={11} aria-hidden="true" /> {councilNames[c] ?? c}
                    </span>
                  ))}
                </div>
              )}
            </section>
            )}

            {/* Filters before the districts are saved: one record, and its first save locks the districts */}
            {isFilters && !locked && (
              <section className="card">
                <p className="card-sub">
                  Choose and save your Local Authority Districts on the <Link to="/coverage">Coverage</Link> page first, then set your filters here.
                </p>
              </section>
            )}

            {/* Which properties (the Filters page) */}
            {isFilters && locked && (
            <section className="card">
              <h2>Which properties</h2>
              {showOwnership && (
                <Field label="Public or private sector" hint="Public sector buildings, private sector business premises, or both.">
                  <Segmented value={ownership} options={ownershipOptions} onChange={(o) => updateFilters({ ownership: o })} />
                </Field>
              )}
              <Field label="System size" hint="Each report costs 1 report credit up to 150 kWp, 1.5 up to 400 kWp, and 2 above.">
                <div className="chip-grid chip-grid-4">
                  {data.options.sizeBands.map((b) => (
                    <ChipToggle key={b.key} on={draft.filters.sizeBands.includes(b.key)} label={b.label} onClick={() => updateFilters({ sizeBands: toggleIn(draft.filters.sizeBands, b.key) })} />
                  ))}
                </div>
              </Field>
              <Field label="Sectors">
                <div className="chip-grid">
                  {data.options.sectors.map((s) => (
                    <ChipToggle key={s.key} on={draft.filters.sectors.includes(s.key)} label={s.label} onClick={() => updateFilters({ sectors: toggleIn(draft.filters.sectors, s.key) })} />
                  ))}
                </div>
              </Field>
              <div className="coverage-grid">
                <Field label="Indicative payback" htmlFor="cov-payback">
                  <select
                    id="cov-payback"
                    className="input"
                    value={draft.filters.maxPaybackYears ?? ""}
                    onChange={(e) => updateFilters({ maxPaybackYears: e.target.value ? Number(e.target.value) : null })}
                  >
                    {PAYBACK_CHOICES.map((y) => (
                      <option key={y ?? "any"} value={y ?? ""}>
                        {y ? `Under ${y} years` : "Any payback"}
                      </option>
                    ))}
                  </select>
                </Field>
                <Field label="Electricity data" hint="Estimates are for private properties and are labelled in red in every report.">
                  <Segmented
                    value={draft.filters.electricityData}
                    options={data.options.electricityData.map((o) => [o.key, o.label] as [typeof o.key, string])}
                    onChange={(electricityData) => updateFilters({ electricityData })}
                  />
                </Field>
              </div>
              {/* No "include overdue" toggle (Neil, 11 Oct 2026): overdue data is no longer a filter, since
                  showing or hiding those properties would say which they are. The saved value is kept as is */}
              <Toggle id="cov-weekly" checked={draft.weeklyEmail} label="Email me new matches every week" onChange={(weeklyEmail) => update({ weeklyEmail })} />
            </section>
            )}

            {/* Coverage saves (and locks) districts while more can be added; Filters saves the filters */}
            {!readOnly && (isFilters ? locked : canAdd) && (
              <div className="profile-actions">
                <button type="submit" className="btn btn-primary" disabled={!dirty || saving}>
                  <Check size={16} aria-hidden="true" /> {saving ? "Saving…" : isFilters ? "Save filters" : "Save coverage"}
                </button>
              </div>
            )}
          </fieldset>

          {/* What the shortlist will use, in words */}
          <aside className="card coverage-summary">
            <h2>Your shortlist covers</h2>
            <Summary
              coverage={draft}
              names={councilNames}
              limit={data.options.limits.councils}
              locked={locked}
              sizeCount={data.options.sizeBands.length}
              sectorCount={data.options.sectors.length}
              showOwnership={showOwnership}
            />
            {counts && (
              <div className="coverage-counts">
                <div className="coverage-count-total">
                  <b>{counts.total.toLocaleString("en-GB")}</b> {counts.total === 1 ? "property matches" : "properties match"}
                </div>
                {draft.councils.length > 1 && (
                  <ul>
                    {draft.councils.map((c) => (
                      <li key={c}>
                        <span>{councilNames[c] ?? c}</span>
                        <b>{(counts.byDistrict[c] ?? 0).toLocaleString("en-GB")}</b>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
            {!counts && <p className="card-sub">Choose a Local Authority District to see how many properties match.</p>}
            {data.updatedAt && (
              <p className="coverage-updated">
                Last saved {new Date(data.updatedAt).toLocaleString("en-GB", { dateStyle: "medium", timeStyle: "short" })}
                {data.updatedBy ? ` by ${data.updatedBy}` : ""}
              </p>
            )}
          </aside>
        </form>
      )}

      {/* First save: councils lock, so ask for an explicit, ticked confirmation */}
      <ConfirmDialog
        open={confirming}
        title={locked ? "Lock in these Local Authority Districts?" : "Lock in your Local Authority Districts?"}
        confirmLabel="Save and lock"
        danger={false}
        busy={saving}
        confirmDisabled={!accepted}
        onConfirm={() => void save(true)}
        onCancel={() => setConfirming(false)}
      >
        <p>
          You're adding <b>{added.map((c) => councilNames[c] ?? c).join(", ")}</b>. Once saved, they <b>can't be removed or changed</b> from the console (you can keep adding
          districts up to your limit of {limit}). You can still change your filters at any time.
        </p>
        <label className="lock-accept">
          <input type="checkbox" checked={accepted} onChange={(e) => setAccepted(e.target.checked)} />
          I understand my Local Authority Districts can't be changed once saved.
        </label>
      </ConfirmDialog>
    </div>
  );
}

/** A selectable pill (aria-pressed), as the prototype's filter chips. */
function ChipToggle({ on, label, onClick }: { on: boolean; label: string; onClick: () => void }) {
  return (
    <button type="button" className={`chip${on ? " is-on" : ""}`} aria-pressed={on} onClick={onClick}>
      {on && <Check size={12} aria-hidden="true" />} {label}
    </button>
  );
}

/** Type-ahead over the ~320 councils; chosen ones show as removable chips, up to the limit. */
function CouncilPicker({
  selected,
  fixed = [],
  options,
  names,
  limit,
  onChange,
}: {
  selected: string[];
  fixed?: string[]; // locked councils: shown with a lock, never removable
  options: { code: string; name: string }[];
  names: Record<string, string>;
  limit: number;
  onChange: (codes: string[]) => void;
}) {
  const [query, setQuery] = useState("");
  const full = selected.length >= limit;
  const q = query.trim().toLowerCase();
  const matches = q && !full ? options.filter((c) => c.name.toLowerCase().includes(q) && !selected.includes(c.code)).slice(0, 8) : [];
  const add = (code: string) => {
    if (full) return;
    onChange([...selected, code]);
    setQuery("");
  };
  return (
    <div className="coverage-picker">
      <Field
        label={`Local Authority Districts (${selected.length} of ${limit})`}
        htmlFor="cov-council"
        hint={full ? `That's your plan's limit of ${limit}.` : `Up to ${limit} Local Authority Districts in England and Wales.`}
      >
        <input
          id="cov-council"
          className="input"
          value={query}
          placeholder={full ? "Limit reached" : "Or type a district, e.g. Bradford"}
          autoComplete="off"
          disabled={full}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => {
            // Enter adds the first match instead of submitting the form
            if (e.key === "Enter") {
              e.preventDefault();
              if (matches[0]) add(matches[0].code);
            }
          }}
        />
      </Field>
      {matches.length > 0 && (
        <ul className="picker-matches" role="listbox">
          {matches.map((c) => (
            <li key={c.code}>
              <button type="button" onClick={() => add(c.code)}>
                <Plus size={12} aria-hidden="true" /> {c.name}
              </button>
            </li>
          ))}
        </ul>
      )}
      <div className="chip-row">
        {selected.length === 0 && <span className="muted">No Local Authority Districts chosen yet.</span>}
        {selected.map((code) =>
          fixed.includes(code) ? (
            <span key={code} className="chip is-on" title="Locked in">
              <Lock size={11} aria-hidden="true" /> {names[code] ?? code}
            </span>
          ) : (
            <span key={code} className="chip is-on">
              {names[code] ?? code}
              <button type="button" aria-label={`Remove ${names[code] ?? code}`} onClick={() => onChange(selected.filter((c) => c !== code))}>
                <X size={12} aria-hidden="true" />
              </button>
            </span>
          ),
        )}
      </div>
    </div>
  );
}

/** The councils and filters in a few lines. */
function Summary({
  coverage,
  names,
  limit,
  locked,
  sizeCount,
  sectorCount,
  showOwnership,
}: {
  coverage: Coverage;
  names: Record<string, string>;
  limit: number;
  locked: boolean;
  sizeCount: number;
  sectorCount: number;
  showOwnership: boolean;
}) {
  return (
    <dl className="coverage-facts">
      <dt>Districts</dt>
      <dd>{coverage.councils.length ? coverage.councils.map((c) => names[c] ?? c).join(", ") : "None chosen yet"}</dd>
      <dt>Status</dt>
      <dd>{locked ? `Locked in (${coverage.councils.length} of ${limit})` : `${coverage.councils.length} of ${limit} chosen, not saved`}</dd>
      {showOwnership && (
        <>
          <dt>Properties</dt>
          <dd>{OWNERSHIP_SUMMARY[coverage.filters.ownership ?? "any"]}</dd>
        </>
      )}
      <dt>Sizes</dt>
      <dd>{coverage.filters.sizeBands.length} of {sizeCount}</dd>
      <dt>Sectors</dt>
      <dd>{coverage.filters.sectors.length} of {sectorCount}</dd>
      <dt>Payback</dt>
      <dd>{coverage.filters.maxPaybackYears ? `Under ${coverage.filters.maxPaybackYears} years` : "Any"}</dd>
      <dt>Electricity data</dt>
      <dd>{coverage.filters.electricityData === "actual" ? "Actual use only" : "Actual and estimated"}</dd>
    </dl>
  );
}
